import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Handshake, DollarSign, CalendarCheck, Activity, ArrowRight, BadgeCheck, Film, Briefcase, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function bandTone(band) {
  if (band === "Élite") return { bg: "bg-gradient-to-br from-gold to-amber-500", ring: "ring-gold", text: "text-petroleum" };
  if (band === "Confiable") return { bg: "bg-emerald-500", ring: "ring-emerald-300", text: "text-white" };
  if (band === "En crecimiento") return { bg: "bg-blue-500", ring: "ring-blue-300", text: "text-white" };
  return { bg: "bg-slate-400", ring: "ring-slate-300", text: "text-white" };
}

// Backend returns band in Spanish. Map to i18n keys.
const BAND_KEY = {
  "Nuevo": "score.band_new",
  "En crecimiento": "score.band_growing",
  "Confiable": "score.band_trusted",
  "Élite": "score.band_elite",
};

const STEP_LABEL_KEY = {
  verify_identity: "score.step_verify_identity",
  verify_pro: "score.step_verify_pro",
  publish_project: "score.step_publish_project",
  publish_brief: "score.step_publish_brief",
  sign_agreement: "score.step_sign_agreement",
  pay_milestone: "score.step_pay_milestone",
};

const STEP_ICONS = {
  shield: ShieldCheck,
  badge: BadgeCheck,
  film: Film,
  briefcase: Briefcase,
  handshake: Handshake,
  dollar: DollarSign,
};

export default function ScoreBadge({ userId, size = "md", theme = "light", showLabel = true }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const isSelf = user?.id && userId && user.id === userId;

  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const { data } = await api.get(`/api/score/${userId}`);
        setData(data);
      } catch (e) {}
    })();
  }, [userId]);

  if (!data) return null;
  const tone = bandTone(data.band);
  const S = { sm: { h: "h-7", txt: "text-xs" }, md: { h: "h-9", txt: "text-sm" }, lg: { h: "h-12", txt: "text-base" } }[size] || {};
  const nextSteps = isSelf ? (data.next_steps || []) : [];
  const bandLabel = BAND_KEY[data.band] ? t(BAND_KEY[data.band]) : data.band;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`score-badge-${userId}`}
          aria-label={`Cinengage Score ${data.score}, ${bandLabel}`}
          className={`inline-flex items-center gap-2 rounded-full ${S.h} pl-2 pr-3 ${tone.bg} ${tone.text} font-semibold ring-2 ${tone.ring} ring-offset-1 ${theme === "dark" ? "ring-offset-petroleum" : "ring-offset-white"} shadow-sm hover:brightness-110 transition`}
        >
          <Sparkles size={size === "lg" ? 16 : 12} />
          <span className={`${S.txt} tabular-nums font-display`}>{data.score}</span>
          {showLabel && <span className={`${S.txt} opacity-90`}>{bandLabel}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0 rounded-2xl border-slate-200 shadow-lg">
        <div className={`p-4 ${tone.bg} ${tone.text} rounded-t-2xl`}>
          <p className="text-[10px] uppercase tracking-widest opacity-90">{t("score.title")}</p>
          <div className="flex items-baseline gap-3 mt-0.5">
            <span className="font-display text-4xl">{data.score}</span>
            <span className="text-sm opacity-90">{t("score.per_100_prefix", { band: bandLabel })}</span>
          </div>
        </div>
        <div className="p-4 space-y-3">
          <Row icon={ShieldCheck} label={t("score.component_verification")} value={data.components.verification.points} max={data.components.verification.max} hint={(data.components.verification.kinds || []).join(", ") || "—"} />
          <Row icon={Handshake} label={t("score.component_agreements")} value={data.components.agreements.points} max={data.components.agreements.max} hint={`${data.components.agreements.count}`} />
          <Row icon={DollarSign} label={t("score.component_milestones")} value={data.components.milestones.points} max={data.components.milestones.max} hint={`${data.components.milestones.count}`} />
          <Row icon={CalendarCheck} label={t("score.component_seniority")} value={data.components.seniority.points} max={data.components.seniority.max} hint={`${data.components.seniority.months} m`} />
          <Row icon={Activity} label={t("score.component_activity")} value={data.components.activity.points} max={data.components.activity.max} hint={`${data.components.activity.count}`} />
        </div>
        <div className="px-4 pb-4">
          <p className="text-[10px] text-slate-400">{t("score.auto_calc")}</p>
        </div>
        {nextSteps.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-3 bg-gradient-to-b from-slate-50 to-white rounded-b-2xl" data-testid="score-next-steps">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={12} className="text-gold" />
              <p className="text-[10px] uppercase tracking-widest text-petroleum font-semibold">{t("score.next_steps_title")}</p>
            </div>
            <ul className="space-y-1.5">
              {nextSteps.map((s) => {
                const Icon = STEP_ICONS[s.icon] || Sparkles;
                const stepLabel = STEP_LABEL_KEY[s.key] ? t(STEP_LABEL_KEY[s.key]) : s.label;
                return (
                  <li key={s.key}>
                    <Link
                      to={s.link}
                      data-testid={`score-next-step-${s.key}`}
                      className="group flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-gold/10 transition"
                    >
                      <span className="flex items-center gap-2 text-xs text-slate-700 group-hover:text-petroleum">
                        <Icon size={12} className="text-gold" />
                        {stepLabel}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="text-[10px] font-display tabular-nums text-emerald-600 font-semibold">{t("score.points_suffix", { points: s.points })}</span>
                        <ArrowRight size={10} className="text-slate-300 group-hover:text-petroleum group-hover:translate-x-0.5 transition" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Row({ icon: Icon, label, value, max, hint }) {
  const pct = Math.min(100, Math.round((value / (max || 1)) * 100));
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-600 inline-flex items-center gap-2">
          <Icon size={12} className="text-gold" /> {label}
        </span>
        <span className="text-xs font-display text-petroleum tabular-nums">{value} / {max}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-gold to-amber-500" style={{ width: `${pct}%` }} />
      </div>
      {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
